/**
 * Which body each fighter wears: mesh files in `public/models/fighters/`, tied
 * to ladder entries.
 *
 * `meshes.json` is keyed by file. Each mesh lists the roster ids that wear it
 * and the Meshy file it was made from, so it can be made again
 * (`tools/makechar.mjs` writes both). `default` is the body anybody without a
 * mesh of their own wears — fighter-a, the lean one, which is how it is
 * claimed without a name of its own.
 *
 * **The game wears it.** `createLadder` carries each fighter's mesh on their
 * runtime record (`meshFor`), and `bookFighter` puts the fighter in it through
 * `combatant.wear` — which rebuilds the slot on that body, since no two bodies
 * share a skeleton. Big Emeka walks out as fighter-b.
 *
 * Pure: handed the manifest, the ladder's roster and the files on disk, it
 * touches nothing.
 */
import MESHES from './meshes.json';
import { HURTBOXES, HURT_PARTS } from './rigBoxes.js';

export { MESHES };

export class MeshManifestError extends Error {}

/**
 * The limbs a move strikes with, each scaled on its own: fighter-a's arms reach
 * 91-94% of the mannequin's and its legs all of it, so one number per body
 * misses by up to 6 cm where four miss by 1 cm on average.
 */
export const REACH_LIMBS = Object.freeze(['leftHand', 'rightHand', 'leftFoot', 'rightFoot']);

/**
 * The mannequin's own reach: what `moves.json` was measured on, and what a body
 * not in the manifest — the fallback — is taken to have.
 */
export const REFERENCE_REACH = Object.freeze({
  leftHand: 1,
  rightHand: 1,
  leftFoot: 1,
  rightFoot: 1,
  against: 0,
});

const FILE = /^[a-z0-9][a-z0-9-]*\.glb$/;

/**
 * Refuses a manifest that does not match the folder or the roster:
 *
 *   - a mesh named by the manifest that is not on disk (a fighter who would
 *     load nothing),
 *   - a mesh on disk that nothing claims (an asset nobody can ever see),
 *   - a roster id the ladder has never heard of, or one fighter in two meshes,
 *   - a default that is not one of the meshes.
 *
 * `files` is the `.glb` names in the folder; `roster` the ladder's entries.
 */
export function validateMeshes(manifest = MESHES, { roster, files }) {
  const meshes = manifest?.meshes ?? {};
  const ids = new Set(roster.map((entry) => entry.id));
  const onDisk = new Set(files);
  const problems = [];

  if (!manifest?.default || !meshes[manifest.default]) {
    problems.push(`the default body "${manifest?.default}" is not one of the meshes`);
  }

  const wearer = new Map();
  for (const [file, mesh] of Object.entries(meshes)) {
    if (!FILE.test(file)) problems.push(`"${file}" is not a mesh file name`);
    if (!onDisk.has(file)) problems.push(`"${file}" is in the manifest and not on disk`);
    if (typeof mesh?.source !== 'string' || !mesh.source) {
      problems.push(`"${file}" does not say what it was made from`);
    }
    const fighters = mesh?.fighters ?? [];
    if (!Array.isArray(fighters)) {
      problems.push(`"${file}" lists its fighters as ${JSON.stringify(fighters)}`);
      continue;
    }
    for (const id of fighters) {
      if (!ids.has(id)) problems.push(`"${file}" is worn by "${id}", who is not on the ladder`);
      if (wearer.has(id)) problems.push(`"${id}" wears both "${wearer.get(id)}" and "${file}"`);
      wearer.set(id, file);
    }
    problems.push(...reachProblems(file, mesh?.reach));
    problems.push(...hurtboxProblems(file, mesh?.hurtboxes));
    if (!fighters.length && file !== manifest?.default) {
      problems.push(`"${file}" is worn by nobody and is not the default`);
    }
  }

  for (const file of files) {
    if (!meshes[file]) problems.push(`"${file}" is on disk and nothing claims it`);
  }

  if (problems.length) throw new MeshManifestError(problems.join('; '));
  return true;
}

/**
 * A body's reach, as `scripts/measure-clips.mjs --bodies --write` measured it:
 * each striking limb as a multiple of the mannequin's, and `against` — metres
 * every move's reach gains (negative: loses) when this body is the one hit,
 * because its body line stands further back or forward of its bones.
 */
function reachProblems(file, reach) {
  if (!reach || typeof reach !== 'object') return [`"${file}" has no measured reach`];
  const problems = [];
  for (const limb of REACH_LIMBS) {
    if (!(reach[limb] >= 0.6 && reach[limb] <= 1.4)) {
      problems.push(`"${file}" reaches ${reach[limb]} with its ${limb}; want 0.6-1.4 of the mannequin's`);
    }
  }
  if (!(Math.abs(reach.against) <= 0.3)) {
    problems.push(`"${file}" moves every blow aimed at it by ${reach.against} m; want within 0.3`);
  }
  return problems;
}

/**
 * A body's own hurtboxes, as `measure-clips.mjs --bodies --write` fitted them
 * (`hurtFit.js`): `[{ name, bone, offset, radius }]`, a name several times
 * where a region is several spheres. Every hurtbox the game prices must be
 * there, each sphere on one of its parts' bones (`HURT_PARTS`) — a body with a part nobody can hit is a body the
 * damage table has stopped describing.
 */
function hurtboxProblems(file, boxes) {
  if (!Array.isArray(boxes) || !boxes.length) return [`"${file}" has no fitted hurtboxes`];
  const problems = [];
  for (const spec of HURTBOXES) {
    const mine = boxes.filter((box) => box.name === spec.name);
    if (!mine.length) problems.push(`"${file}" has no ${spec.name} hurtbox`);
    for (const box of mine) {
      const bones = HURT_PARTS[spec.name].map((part) => part.on);
      if (!bones.includes(box.bone)) problems.push(`"${file}" hangs ${spec.name} on ${box.bone}, not ${bones.join(' or ')}`);
      if (!(box.radius >= 0.03 && box.radius <= 0.5)) problems.push(`"${file}" sizes ${spec.name} at ${box.radius} m`);
      if (!(Array.isArray(box.offset) && box.offset.length === 3 && box.offset.every((n) => Math.abs(n) < 1))) {
        problems.push(`"${file}" places ${spec.name} at ${JSON.stringify(box.offset)}`);
      }
    }
  }
  for (const box of boxes) {
    if (!HURTBOXES.some((spec) => spec.name === box.name)) problems.push(`"${file}" has a hurtbox "${box.name}" nothing prices`);
  }
  return problems;
}

/**
 * The hurtboxes of the body at `url`: its fitted spheres, each carrying the
 * worth (`damageScale`, `consciousnessMultiplier`) `rigBoxes.js` gives its
 * name; or the mannequin's own for a body the manifest does not know.
 */
export function hurtboxesOf(url, manifest = MESHES) {
  const file = String(url ?? '').split('/').pop();
  const fitted = manifest.meshes[file]?.hurtboxes;
  if (!fitted) return HURTBOXES;
  return fitted.map((box) => ({ ...HURTBOXES.find((spec) => spec.name === box.name), ...box }));
}

/** The reach of the body at `url` (a path or a URL ending in the file name). */
export function reachOf(url, manifest = MESHES) {
  const file = String(url ?? '').split('/').pop();
  return manifest.meshes[file]?.reach ?? REFERENCE_REACH;
}

/** The mesh file a roster id wears: their own, or the default. */
export function meshFor(id, manifest = MESHES) {
  for (const [file, mesh] of Object.entries(manifest.meshes)) {
    if (mesh.fighters?.includes(id)) return file;
  }
  return manifest.default;
}
